'use client';

import { useMemo } from 'react';
import type { AiComponentTypeType } from '@terra/shared-schemas-orchestrator';
import {
  APPLICATION_LEVEL_NODE_ID,
  collectSubtreeEndpointIds,
  DomainTreeNodeType,
  type DomainTree,
} from '../utils/domain-tree.utils';

type TreeFilterParamsOptions = {
  useRegexPatterns?: boolean;
};

export type DomainTreeSelectionFilter = {
  endpointIds?: string[];
  assetIds?: string[];
  componentTypes?: AiComponentTypeType[];
  urlPatterns?: string[];
  includeApplicationLevelGroup: boolean;
  isFilterPending: boolean;
};

type TreeFilterParamsResult<T extends boolean = false> = T extends true
  ? {
      endpointIds: Set<string> | null;
      urlRegexPatterns: RegExp[] | null;
      includeApplicationLevelGroup: boolean;
      isFilterPending: boolean;
    }
  : DomainTreeSelectionFilter;

export function useTreeFilterParams<T extends boolean = false>(
  selectedNodes: string[],
  domainTree: DomainTree | null,
  generateUrlPatterns: (selectedNodeIds: string[]) => string[] | undefined,
  options?: TreeFilterParamsOptions & { useRegexPatterns?: T },
): TreeFilterParamsResult<T> {
  const useRegexPatterns = options?.useRegexPatterns ?? false;

  return useMemo(() => {
    const includeApplicationLevelGroup = selectedNodes.includes(
      APPLICATION_LEVEL_NODE_ID,
    );
    const treeSelection = selectedNodes.filter(
      (id) => id !== APPLICATION_LEVEL_NODE_ID,
    );

    if (treeSelection.length === 0 && !includeApplicationLevelGroup) {
      if (useRegexPatterns) {
        return {
          endpointIds: null,
          urlRegexPatterns: null,
          includeApplicationLevelGroup: false,
          isFilterPending: false,
        } as TreeFilterParamsResult<T>;
      }
      return {
        endpointIds: undefined,
        urlPatterns: undefined,
        includeApplicationLevelGroup: false,
        isFilterPending: false,
      } as TreeFilterParamsResult<T>;
    }

    if (treeSelection.length === 0 && includeApplicationLevelGroup) {
      if (useRegexPatterns) {
        return {
          endpointIds: null,
          urlRegexPatterns: null,
          includeApplicationLevelGroup: true,
          isFilterPending: false,
        } as TreeFilterParamsResult<T>;
      }
      return {
        endpointIds: undefined,
        urlPatterns: undefined,
        includeApplicationLevelGroup: true,
        isFilterPending: false,
      } as TreeFilterParamsResult<T>;
    }

    if (!domainTree) {
      if (useRegexPatterns) {
        return {
          endpointIds: null,
          urlRegexPatterns: null,
          includeApplicationLevelGroup,
          isFilterPending: true,
        } as TreeFilterParamsResult<T>;
      }
      return {
        endpointIds: undefined,
        urlPatterns: undefined,
        includeApplicationLevelGroup,
        isFilterPending: true,
      } as TreeFilterParamsResult<T>;
    }

    const collectedEndpointIds = new Set<string>();
    let hasDomainOrPathNode = false;

    for (const nodeId of treeSelection) {
      const node = domainTree.nodeMap.get(nodeId);

      if (!node && nodeId.startsWith('endpoint:')) {
        const parts = nodeId.split(':');
        const assetId = parts[parts.length - 1];
        if (assetId) {
          collectedEndpointIds.add(assetId);
        }
        continue;
      }

      if (!node) {
        hasDomainOrPathNode = true;
        continue;
      }

      if (
        node.nodeType === DomainTreeNodeType.Path &&
        node.children.length > 0
      ) {
        collectSubtreeEndpointIds(node).forEach((endpointId) => {
          collectedEndpointIds.add(endpointId);
        });
        continue;
      }

      if (node.nodeType === DomainTreeNodeType.Endpoint) {
        if (node.children.length > 0) {
          hasDomainOrPathNode = true;
          continue;
        }

        if (node.assetId) {
          collectedEndpointIds.add(node.assetId);
          continue;
        }

        if (node.directEndpointIds.length > 0) {
          node.directEndpointIds.forEach((endpointId) => {
            collectedEndpointIds.add(endpointId);
          });
          continue;
        }

        let hadEndpointAsset = false;
        node.assets.forEach((asset) => {
          if (asset.type === 'endpoint') {
            collectedEndpointIds.add(asset.data.id);
            hadEndpointAsset = true;
          }
        });
        if (!hadEndpointAsset) {
          hasDomainOrPathNode = true;
        }
        continue;
      }

      hasDomainOrPathNode = true;
    }

    if (hasDomainOrPathNode) {
      const patterns = generateUrlPatterns(treeSelection);

      if (useRegexPatterns) {
        if (!patterns || patterns.length === 0) {
          return {
            endpointIds: null,
            urlRegexPatterns: null,
            includeApplicationLevelGroup,
            isFilterPending: false,
          } as TreeFilterParamsResult<T>;
        }
        const urlRegexPatterns: RegExp[] = [];
        for (const pattern of patterns) {
          try {
            urlRegexPatterns.push(new RegExp(pattern));
          } catch {
            return {
              endpointIds: null,
              urlRegexPatterns: null,
              includeApplicationLevelGroup,
              isFilterPending: false,
            } as TreeFilterParamsResult<T>;
          }
        }
        return {
          endpointIds: null,
          urlRegexPatterns,
          includeApplicationLevelGroup,
          isFilterPending: false,
        } as TreeFilterParamsResult<T>;
      }

      return {
        endpointIds: undefined,
        urlPatterns: patterns,
        includeApplicationLevelGroup,
        isFilterPending: false,
      } as TreeFilterParamsResult<T>;
    }

    if (collectedEndpointIds.size === 0) {
      if (useRegexPatterns) {
        return {
          endpointIds: null,
          urlRegexPatterns: null,
          includeApplicationLevelGroup,
          isFilterPending: false,
        } as TreeFilterParamsResult<T>;
      }
      return {
        endpointIds: undefined,
        urlPatterns: undefined,
        includeApplicationLevelGroup,
        isFilterPending: false,
      } as TreeFilterParamsResult<T>;
    }

    if (useRegexPatterns) {
      return {
        endpointIds: collectedEndpointIds,
        urlRegexPatterns: null,
        includeApplicationLevelGroup,
        isFilterPending: false,
      } as TreeFilterParamsResult<T>;
    }
    return {
      endpointIds: [...collectedEndpointIds],
      urlPatterns: undefined,
      includeApplicationLevelGroup,
      isFilterPending: false,
    } as TreeFilterParamsResult<T>;
  }, [selectedNodes, domainTree, generateUrlPatterns, useRegexPatterns]);
}
